import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Action } from '../../models/core/action';
import { BaseComponent } from '../base/base.component';
import { IconComponent } from '../core/labels/icon.component';

@Component({
  selector: 'app-external-distributed-reports-header',
  imports: [
    RouterModule,
    BadgeModule,
    MenuModule,
    OverlayBadgeModule,
    IconComponent,
  ],
  template: ` <header
    class="tw-w-full tw-flex tw-flex-wrap tw-justify-center tw-bg-[var(--sed-grey-20-color)]"
    [class.tw-shadow-lg]="hasScrolled()"
    role="banner"
  >
    <div class="tw-max-w-[1300px] tw-flex-col tw-w-full tw-grow">
      <div class="tw-flex tw-flex-full tw-flex-row tw-justify-between">
        <div class="tw-flex tw-p-6 tw-items-center tw-justify-start">
          <img
            src="assets/images/omni-logo.png"
            class="logo tw-pr-4"
            alt="Sedgwick Logo"
          />
        </div>
        <div class="tw-justify-end tw-flex tw-p-6 tw-items-center md:tw-flex">
          <button
            type="button"
            class="user-menu-button tw-flex tw-items-center tw-cursor-pointer tw-pt-0 tw-bg-transparent tw-border-none"
            aria-label="logout menu, has 1 menu item"
            [attr.aria-expanded]="isUserMenuExpanded"
            aria-haspopup="menu"
            (click)="menuClick({ key: 'logout' })"
            (keydown.enter)="menuClick({ key: 'logout' })"
            (keydown.space)="
              menuClick({ key: 'logout' }); $event.preventDefault()
            "
          >
            <app-icon
              name="fa-right-from-bracket fa-light"
              size="18px"
              class="tw-ml-2"
              color="black"
              ariaLabel=""
            ></app-icon>

            <div class="tw-pl-3 tw-hidden md:tw-block">
              <b>Log out</b>
            </div>
          </button>
        </div>
      </div>
    </div>
  </header>`,
  styles: `
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      background-color: var(--sed-grey-20-color);
    }

    .message {
      background-color: var(--color-accent-text);
      color: white;
      font-weight: bold;
    }

    .logo {
      height: 38px;
    }

    @media (max-width: 768px) {
      .logo {
        height: 15px;
      }
    }

    .icon-size {
      font-size: 2rem;
    }
  `,
})
export class ExternalDistributedReportsHeaderComponent
  extends BaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private headerObserver: ResizeObserver | null = null;

  hasScrolled = signal(false);
  isUserMenuExpanded = false;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateScrollState();
  }

  constructor() {
    super();
  }

  ngAfterViewInit(): void {
    const host = this.el.nativeElement as HTMLElement;
    this.headerObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${host.offsetHeight}px`,
      );
    });
    this.headerObserver.observe(host);
    this.destroyRef.onDestroy(() => this.headerObserver?.disconnect());
  }

  private updateScrollState(): void {
    this.hasScrolled.set(window.scrollY > 0);
  }

  onUserMenuVisibilityChange(isExpanded: boolean) {
    this.isUserMenuExpanded = isExpanded;
  }

  menuClick(action: Action) {
    if (action.key === 'logout') {
      this.session.logout();
      return;
    }
  }

  ngOnInit(): void {
    this.updateScrollState();
  }

  override ngOnDestroy(): void {
    // Cleanup if needed
    super.ngOnDestroy();
  }
}
